// `chrome-extension.extension-tabs.shortlist.candidate.profile` — one candidate's profile
// opened by id (plan §7 phase 3, slice 4) — AND its twin
// `chrome-extension.extension-tabs.shortlist.hunter.review.candidate.profile`, the same
// snippet inside the review screen's side view.
//
// ⚠️ ONE COMPONENT, BECAUSE THE DIFFERENCE IS AN ALERT AND NOT A SCREEN. Upstream declares two
// states whose view templates are the same component with different bindings; `viaHunter` is
// those bindings. Without an alert the candidate comes from
// `getCandidatesIgnoreFiltersAlertless`, the preview call goes out without an `alertId`, and
// the summary is absent by design rather than by omission. With one, all three change — see
// `CandidateProfileProps`.
import { useEffect, useState, type ReactElement } from 'react'
import { useLocation, useNavigate, useOutlet, useOutletContext, useSearchParams } from 'react-router-dom'
import {
  downloadWooProfile,
  favorCandidates,
  fetchViewModel,
  getCandidateEmailIntegrationDetails,
  getCandidateByAlertIdIgnoreFilters,
  getCandidateProfilePreviewAndMatchingSummary,
  getCandidatesIgnoreFiltersAlertless,
  hideCandidates,
  saveEnrichmentReport,
  sendCandidateDetails,
  unfavorCandidate,
  unhideCandidate,
  type AlertCandidateProfile,
  type CandidateProfilePreview,
  type ExportListTarget,
} from '@woo-ext/api'
import { ExtensionViewWidth } from '@woo-ext/protocol'
import {
  applyFavor,
  applyHide,
  applyUnfavor,
  applyUnhide,
  candidateName,
  CandidateSnippet,
  CANDIDATE_DETAILS_IN_PROGRESS_MESSAGE,
  candidateDetailsResultMessage,
  PROFILE_ACTION_OK_BUTTON_TEXT,
  ProfileAction,
  ProfileActionConfirmationModal,
  REPORT_ENRICHMENT_SENT_DIALOGUE_DELAY_MS,
  ReportEnrichmentModal,
  ReportEnrichmentSentDialogue,
  saveFile,
  profileActions,
  type ProfileActionDefinition,
  getAtsLabel,
  exportTitle,
  exportToExistingListEnabled,
  singleProfileExportEnabled,
  singleProfileNewListTarget,
  SingleProfileExportModal,
  type SingleProfileExportOutcome,
} from '@woo-ext/ui'
import { panelBase } from './panelBase.ts'
import { trackCandidate, trackCandidateSelection, trackCandidateSnippet } from './actionTracking.ts'
import { publishPageEntity } from '../pageEntity.ts'
import { useProfileScrollTracking } from './useProfileScrollTracking.ts'
import { sendAssignToJob, sendOpenHotlist } from './assignToJob.ts'
import { emitShortlistFlagEvent, requestShortlistExport, requestShortlistMessage } from './shortlistEvents.ts'
import { isInViewMore, viewLessQuery, viewMoreQuery } from './profileViewMore.ts'
import { publishTabName } from './tabNames.ts'

/** The flags this screen's icon strip and ATS card sit behind. */
const ADD_TO_JOB = 'ADD_TO_JOB'
const ASSIGN_TO_JOB = 'ASSIGN_TO_JOB'
const SHORTLIST_MESSAGE_CANDIDATES = 'SHORTLIST_MESSAGE_CANDIDATES'
const EXTENSION_DOWNLOAD_WOO_PROFILE_ENABLED = 'EXTENSION_DOWNLOAD_WOO_PROFILE_ENABLED'
const SEND_CANDIDATE_DETAILS_TO_USER = 'SEND_CANDIDATE_DETAILS_TO_USER'

/**
 * `AtsInfoFeatureFlagHelperService.isATSInfoEnabled` — three flags, ORed, and NONE of them is
 * about the ATS.
 *
 * ⚠️ THE "<ATS> INFORMATION" CARD IS GATED ON THE LINKEDIN AND INDEED INTEGRATIONS. The card
 * shows Bullhorn data and appears only when the tenant has LinkedIn (either flag) or Indeed
 * sourcing switched on — because it exists to tell a recruiter looking at a candidate found
 * OUTSIDE their ATS what their ATS already knows. A tenant with neither has the data in the
 * response and no card. Guessing this one from its name gives a card on every tenant.
 */
const EXTENSION_LINKEDIN_ENABLED = 'EXTENSION_LINKEDIN_ENABLED'
const EXTENSION_LINKEDIN_SEARCH_ENABLED = 'EXTENSION_LINKEDIN_SEARCH_ENABLED'
const EXTENSION_INDEED_SEARCH_ENABLED = 'EXTENSION_INDEED_SEARCH_ENABLED'

/** `addToJobATSCopyOptions` — Bullhorn names itself in the tooltip; nobody else does. */
function addToJobTitle(atsName: string | undefined): string {
  return atsName === 'Bullhorn' ? 'Add to Bullhorn job' : 'Add to job'
}

function useCandidateId(): number | undefined {
  const [params] = useSearchParams()
  const raw = params.get('candidateId')
  if (raw === null) return undefined
  const parsed = Number.parseInt(raw, 10)
  return Number.isNaN(parsed) ? undefined : parsed
}

export interface CandidateProfileProps {
  /**
   * `shortlistSessionInfo.viaHunter` — this profile opened from a search's candidate list.
   *
   * ⚠️ IT CHANGES THREE THINGS AT ONCE, and the file header's "the whole difference" names the
   * first: the candidate is fetched WITHIN the alert (`getCandidateByAlertIdIgnoreFilters`,
   * which `EntityContainerBase` picks on `angular.isNumber($stateParams.alertId)`), the preview
   * call carries the `alertId` so the server answers with a matching summary, and
   * `profileActions` adds Favor and Archive — `viaHunter` is literally `$state.includes(Hunter)`
   * upstream, and those two icons exist only inside a search.
   */
  viaHunter?: boolean
}

export function CandidateProfile({ viaHunter = false }: CandidateProfileProps = {}): ReactElement {
  const [params] = useSearchParams()
  // ⚠️ THE ALERT'S NAME COMES FROM THE PARENT, WHICH IS WHAT UPSTREAM'S RESOLVE DOES.
  // `hunterContainer` is resolved at the review state and injected into this state's
  // controller; the review route supplies the same thing through the outlet context, so the
  // card's "For <name> hunter" costs no request. Null when this route is the standalone one.
  const inherited = useOutletContext<{ alertName?: string } | null>()
  const candidateId = useCandidateId()
  const rawAlert = params.get('alertId')
  const parsedAlert = rawAlert === null ? Number.NaN : Number.parseInt(rawAlert, 10)
  const alertId = Number.isNaN(parsedAlert) ? undefined : parsedAlert
  // ⚠️ THE FOUR CHILDREN DO NOT FILL THE SAME HOLE, so one outlet cannot serve them all.
  // `more` targets `shortlist-candidate-profile-view@…`, which is TRANSCLUDED INSIDE the
  // snippet and widens it to Extended + Detailed; add-to-job, candidate-messages and
  // email-signin target `shortlist-candidate-profile-side-view@…`, a sibling of the snippet.
  // Telling them apart by the path is what upstream's two named views do structurally.
  const onMoreRoute = useLocation().pathname.endsWith('/profile/more')
  /** `ExtensionState.CandidateProfile` exactly: standalone, not More, not inside the review. */
  const standaloneState = !viaHunter && !onMoreRoute

  // ⚠️ BEING ON `/more` IS NOT ENOUGH TO OPEN THE MORE VIEW, AND THE REFERENCE CAPTURE PROVES
  // IT: `/candidate/profile/more?candidateId=15115` renders the NARROW layout, with the button
  // still reading "More ›" and the contact lines still in the column. The snippet's
  // `isInViewMoreState()` is `$state.includes(viewMoreState, {alertId, candidateId, algoScore})`
  // — a PARAMS comparison — and `algoScore` is `parseFloat($stateParams.algoScore)`. With no
  // `algoScore` on the URL nothing matches and the predicate is false. Opening the wide layout on
  // the path alone is a different screen from the one production renders for the same URL.
  // ⚠️ BUT THE TEXT "NaN" DOES MATCH: measured 29 Oct, `…/more?candidateId=…&algoScore=NaN` — the
  // URL upstream's own More button lands on — reads "Less". See `profileViewMore.ts`.
  const rawAlgoScore = params.get('algoScore')
  const viewMore = isInViewMore(onMoreRoute, rawAlgoScore)

  const [candidate, setCandidate] = useState<AlertCandidateProfile | undefined>(undefined)
  const [preview, setPreview] = useState<CandidateProfilePreview | undefined>(undefined)
  const [loadingPreview, setLoadingPreview] = useState(true)
  const [atsName, setAtsName] = useState<string | undefined>(undefined)
  const [atsLabel, setAtsLabel] = useState<string | undefined>(undefined)
  const [relevantDays, setRelevantDays] = useState<number | undefined>(undefined)
  const [atsInfoEnabled, setAtsInfoEnabled] = useState(false)
  const [actions, setActions] = useState<readonly ProfileActionDefinition[]>([])
  /** "Email candidate details to me" — its confirmation modal is open. */
  const [sendingDetails, setSendingDetails] = useState(false)
  /** "Report" — the enrichment-report modal, then (a second later) its success dialogue. */
  const [reporting, setReporting] = useState(false)
  /**
   * SINGLE_PROFILE_EXPORT (cee70a51e8): `exportToExistingListEnabled()` decides whether Export
   * opens the list picker (`…/profile/export`) or exports straight to a new list named after the
   * candidate. `exporting` is the run in flight — its key remounts the dialogue per export.
   */
  const [exportOpensPicker, setExportOpensPicker] = useState(false)
  const [exporting, setExporting] = useState<{ key: number; target: ExportListTarget } | undefined>(undefined)
  const [reportSent, setReportSent] = useState(false)

  /**
   * The snippet's six-icon strip (`extension-alert-candidate-snippet.component.ng1.ts:355`).
   *
   * ⚠️ THE STRIP WAS DRAWN AND INERT. `ProfileActionsStrip` has always had `onClick`, and
   * `CandidateSnippet` has always forwarded `onAction` — this route just never passed one, so
   * every icon rendered with `cursor-pointer` and did nothing. Same shape as the candidate
   * rows (`499da95`): a wired component behind an unwired call site.
   *
   * ⚠️ FAVOUR AND ARCHIVE ARE TOGGLES HERE AND ADD-ONLY ON THE SELECTION BAR. Upstream pairs
   * `favorAlertEntity`/`unfavorAlertEntity` and `hideAlertEntity`/`unhideAlertEntity` on the
   * snippet because it acts on ONE candidate; the bulk endpoints have no inverse because they
   * act on a list. `unfavorCandidate` had to be ported for this.
   *
   * The four outward-facing actions are deliberately NOT handled yet: Message, Export,
   * Add-to-job and Assign-to-job reach the customer's ATS or send mail, and they are the
   * subject of the VERIFY card — wiring them here would fire writes nobody has confirmed.
   */
  const navigate = useNavigate()
  const { pathname: currentPath, search: currentSearch } = useLocation()

  /**
   * ⚠️ THE PANEL BELONGS TO THE SCREEN THAT OWNS THE SNIPPET, NOT TO THE SNIPPET. Inside the
   * review's side view the strip's events are handled by the REVIEW component, whose
   * `addToJobState` is `HunterReviewAddToJob` (`extension-alert-candidates.component.ng1.ts:237`)
   * — `…/review/add-to-job`, a sibling of the profile rather than a child of it. The standalone
   * profile has its own three children. `routes.json` agrees: there is no
   * `…/review/candidate/profile/add-to-job`, so deriving the base from the current path alone
   * navigated to a route that does not exist.
   */
  const panelPath = (leaf: string, extra?: Record<string, string>): string => {
    const base = panelBase(currentPath)
    const query = new URLSearchParams(currentSearch)
    for (const [key, value] of Object.entries(extra ?? {})) query.set(key, value)
    return `${base}/${leaf}?${query.toString()}`
  }

  /**
   * `toggleViewMore` (`extension-alert-candidate-snippet.component.ng1.ts:300`) — and it was
   * passed by nobody, so "More ›" rendered with a chevron and a pointer cursor and did
   * nothing on every screen that draws it. `goToViewMoreState` adds `originSnippet`, a
   * declared boolean param on the `profile` state that nothing in the SPA reads back; it is
   * transcribed rather than dropped because the URL is what careero's action tracking
   * records.
   */
  const onToggleViewMore = (): void => {
    const query = new URLSearchParams(currentSearch)
    // ⚠️ THE PREDICATE IS `viewMore`, NOT THE PATH, and the difference is visible on the very
    // URL the fixtures use. `toggleViewMore` asks `isInViewMoreState()` — the same params
    // comparison that decides whether the button reads "More" or "Less" — so on
    // `…/profile/more` with no `algoScore` the button still says "More" and must still go
    // FORWARD. Keying the toggle on the path made it read "More" and act as "Less".
    if (viewMore) {
      // `originSnippet` stays: `$state.go` inherits it (`profileViewMore.ts`).
      void navigate(`${currentPath.replace(/\/more$/, '')}?${viewLessQuery('candidateId', query)}`)
      return
    }
    const base = onMoreRoute ? currentPath : `${currentPath}/more`
    const target = `${base}?${viewMoreQuery('candidateId', query)}`
    // ui-router ignores a transition to the state it is already in; `navigate` does not.
    if (target === `${currentPath}${currentSearch}`) return
    void navigate(target)
  }

  /**
   * `runExport` — the profile owns the export's outcome. A rejected start STAYS (eace2a0e4b): the
   * user is left on the picker to retry. Otherwise back to the profile, flagging the candidate
   * when the ATS confirmed it.
   */
  const runExport = (target: ExportListTarget): void => {
    setExporting((previous) => ({ key: (previous?.key ?? 0) + 1, target }))
  }
  const onExportSettled = (outcome: SingleProfileExportOutcome): void => {
    setExporting(undefined)
    if (outcome === 'failed') return
    if (outcome === 'exported') {
      setCandidate((previous) => (previous === undefined ? previous : { ...previous, exported: true }))
    }
    // `$state.go(CandidateProfile, { candidateId })` — from the picker, or a no-op from the profile.
    const profilePath = panelBase(currentPath)
    if (profilePath !== currentPath) void navigate(`${profilePath}${currentSearch}`)
  }

  const onProfileAction = (action: ProfileActionDefinition): void => {
    // ⚠️ EXPORT NEEDS NO ALERT, AND MUST NOT WAIT FOR ONE. It is the standalone profile's action
    // above all — the LinkedIn card, opened by id — so it is handled before the alert guard below.
    if (action.name === ProfileAction.Export && candidate !== undefined) {
      // Inside the review the LIST exports it (`OnSingleCandidateExportClick` → `onExportCandidate`):
      // it ticks the candidate and exports the selection, exactly as the row's Export does.
      if (viaHunter) {
        if (alertId !== undefined) requestShortlistExport({ entity: 'candidates', alertId, row: candidate })
        return
      }
      // `onExportClick` — track, then the picker or a new list named after the candidate.
      trackCandidate.exportClicked(candidate, 'profile')
      if (exportOpensPicker) {
        void navigate(panelPath('export'))
        return
      }
      runExport(singleProfileNewListTarget(candidate.name))
      return
    }

    if (candidate === undefined || alertId === undefined) return
    const id = candidate.id

    // ⚠️ THE TWO PANEL ACTIONS NAVIGATE; THEY DO NOT WRITE. `onSingleAddToJobInAts` and
    // `onSingleCandidateMessageClick` publish an event the owning screen turns into a
    // `$state.go` (`shortlist.candidate-add-to-job.service.ng1.ts:122`,
    // `shortlist.candidate-message.service.ng1.ts:66`) — it is the PANEL that posts, and its
    // submit is the button this port leaves inert. Measured beside build 354: both icons did
    // nothing at all here, so the strip was four-fifths decoration.
    if (action.name === ProfileAction.AddToJob) {
      trackCandidate.addToJob(candidate)
      // Inside the review the list ticks the candidate (`onCandidateChecked`), which it records.
      if (viaHunter) trackCandidateSelection.checked(alertId, candidate, true)
      void navigate(panelPath('add-to-job'))
      return
    }

    if (action.name === ProfileAction.Message) {
      // Inside the review the LIST messages it (`onSingleCandidateMessageClick` → its
      // `messageCandidate`): selection, tracking and the composer are the review's.
      if (viaHunter && requestShortlistMessage({ entity: 'candidates', alertId, row: candidate })) return
      trackCandidate.message(candidate)
      void getCandidateEmailIntegrationDetails().then(
        (details) => {
          if (details.enabled) {
            void navigate(panelPath('candidate-messages'))
            return
          }
          void navigate(
            panelPath(
              'email-signin',
              details.emailServerType === null ? undefined : { emailServerType: details.emailServerType },
            ),
          )
        },
        () => undefined,
      )
      return
    }

    // ⚠️ THE ONLY ACTION IN THE STRIP THAT PRODUCES A FILE RATHER THAN A SCREEN, and a plain
    // GET — `saveFile(`/alert-candidate/downloadWooProfile?candidateId=…`)`.
    if (action.name === ProfileAction.Download) {
      trackCandidate.download(candidate, 'profile', alertId)
      void downloadWooProfile(id).then(saveFile, () => undefined)
      return
    }

    // `assignToJobInAts` — the parent frame does the assigning; then `closeSnippetWindow()`,
    // which returns to the review only from inside it (`isInSnippetView`).
    if (action.name === ProfileAction.AssignToJob) {
      trackCandidate.assignToJob(candidate, 'profile')
      sendAssignToJob(candidate, alertId)
      if (viaHunter) void navigate(`/chrome-extension/extension/shortlist/review?alertId=${String(alertId)}`)
      return
    }

    // `sendCandidateDetails` — mails the recruiter; the confirmation modal runs the request.
    if (action.name === ProfileAction.SendCandidateDetails) {
      trackCandidate.emailDetails(candidate, 'profile')
      setSendingDetails(true)
      return
    }

    // `onReportEnrichment` (snippet:522).
    if (action.name === ProfileAction.ReportEnrichment) {
      trackCandidate.reportEnrichment(candidate)
      setReporting(true)
      return
    }

    if (action.name === ProfileAction.Favor) {
      const undo = candidate.favorite === true
      if (undo) trackCandidate.unfavor(candidate, 'profile')
      else trackCandidate.favor(candidate, 'profile')
      const call = undo ? unfavorCandidate(alertId, id) : favorCandidates(alertId, [id])
      void Promise.resolve(call).then(
        () => {
          setCandidate((previous) =>
            previous === undefined ? previous : undo ? applyUnfavor(previous) : applyFavor(previous),
          )
          emitShortlistFlagEvent({ entity: 'candidates', alertId, ids: [id], kind: undo ? 'unfavor' : 'favor' })
        },
        () => undefined,
      )
      return
    }

    if (action.name === ProfileAction.Archive) {
      if (candidate.hidden === true) trackCandidate.unhide(candidate, 'profile')
      else trackCandidate.hide(candidate, 'profile')
      if (candidate.hidden === true) {
        void unhideCandidate(alertId, id).then(
          () => {
            setCandidate((previous) => (previous === undefined ? previous : applyUnhide(previous)))
            emitShortlistFlagEvent({ entity: 'candidates', alertId, ids: [id], kind: 'unhide' })
          },
          () => undefined,
        )
        return
      }
      void hideCandidates(alertId, [id]).then(
        (hiddenDate) => {
          setCandidate((previous) => (previous === undefined ? previous : applyHide(previous, hiddenDate)))
          // `OnCandidateHidden` — the review behind this profile updates the row and, unless its
          // filter shows archived rows, closes this snippet (`removeCandidatesFromListAndRefreshPage`).
          emitShortlistFlagEvent({ entity: 'candidates', alertId, ids: [id], kind: 'hide', hiddenDate })
        },
        () => undefined,
      )
    }
  }

  // `more` and the three side-by-side children are nested routes; an outlet means one is open.
  //
  // ⚠️ THE CONTEXT IS NOT CONVENIENCE — IT IS THE STATE'S `init()`. `CandidateProfileAddToJob`
  // seeds `entityCheckboxContainer` with exactly this candidate before its panel renders, which
  // is why the panel's heading reads the candidate's name where the review screen's reads the
  // list selection. Passing the candidate down IS that seeding.
  //
  // `onExport` is the `OnCandidatesExport` the export panel emits: the profile runs it.
  const child = useOutlet(
    candidate === undefined
      ? undefined
      : { checked: [{ id: candidate.id, name: candidate.name }], onExport: runExport },
  )

  useEffect(() => {
    let live = true
    fetchViewModel().then(
      (model) => {
        if (!live) return
        const name = model.atsName ?? undefined
        setAtsName(name)
        setAtsLabel(getAtsLabel(model))
        setExportOpensPicker(exportToExistingListEnabled(model))
        setRelevantDays(model.relevantDaysForEnriched)
        setAtsInfoEnabled(
          model.featureFlags?.[EXTENSION_LINKEDIN_ENABLED] === true ||
            model.featureFlags?.[EXTENSION_LINKEDIN_SEARCH_ENABLED] === true ||
            model.featureFlags?.[EXTENSION_INDEED_SEARCH_ENABLED] === true,
        )
        setActions(
          profileActions({
            downloadWooProfileEnabled: model.featureFlags?.[EXTENSION_DOWNLOAD_WOO_PROFILE_ENABLED] === true,
            // `viaHunter` is `$state.includes(Hunter)`: false on the standalone profile, where
            // Favor and Archive are absent, and true on the review screen's twin.
            viaHunter,
            assignToJobEnabled: model.featureFlags?.[ASSIGN_TO_JOB] === true && model.assignToJobEnabled === true,
            addToAtsJobEnabled: model.featureFlags?.[ADD_TO_JOB] === true && model.addToJobEnabled === true,
            messageCandidatesEnabled: model.featureFlags?.[SHORTLIST_MESSAGE_CANDIDATES] === true,
            sendCandidateDetailsEnabled: model.featureFlags?.[SEND_CANDIDATE_DETAILS_TO_USER] === true,
            // Upstream gates Report on a handler being passed, and the snippet always passes
            // one — so it is unconditional, not flagged.
            reportEnrichmentEnabled: true,
            // The snippet always wires `exportToAtsList`, so the flag alone decides (cee70a51e8).
            singleProfileExportEnabled: singleProfileExportEnabled(model),
            exportTitle: exportTitle(name),
            atsLabel: getAtsLabel(model),
            addToJobTitle: addToJobTitle(name),
          }),
        )
      },
      () => undefined,
    )
    return () => {
      live = false
    }
  }, [viaHunter])

  useEffect(() => {
    if (candidateId === undefined) return undefined
    let live = true

    const loading =
      viaHunter && alertId !== undefined
        ? getCandidateByAlertIdIgnoreFilters({ alertId, candidateId })
        : getCandidatesIgnoreFiltersAlertless({ limitCandidateIds: [candidateId] })
    loading.then(
      (page) => {
        if (!live) return
        const loaded = page.candidatePage[0]
        setCandidate(loaded)
        // `data.getTabName` — the tab is named after the candidate, and the fallback for a row
        // that will not load is upstream's own string rather than an empty tab.
        publishTabName('candidate', loaded === undefined ? 'Candidate profile' : candidateName(loaded))
      },
      () => {
        if (live) publishTabName('candidate', 'Candidate profile')
      },
    )

    // ⚠️ THE TWO CALLS ARE INDEPENDENT AND THE SCREEN RENDERS BETWEEN THEM. Upstream runs them
    // through one `$q.all`, but each sets its own field and the template reads them separately:
    // the header appears with the candidate while the highlights column still spins. Awaiting
    // both before drawing anything would hold the whole screen on the slower of the two.
    // ⚠️ THE `alertId` IS WHAT PRODUCES THE MATCHING SUMMARY, and it is optional on purpose:
    // the summary is a statement about a search, so the standalone profile asks without one
    // and the server answers with the preview alone. Sending it here is not an optimisation —
    // it is the difference between the hunter screen's highlights column and the plain one.
    getCandidateProfilePreviewAndMatchingSummary({
      candidateId,
      ...(viaHunter && alertId !== undefined ? { alertId } : {}),
    }).then(
      (result) => {
        if (!live) return
        setPreview(result)
        setLoadingPreview(false)
      },
      () => {
        // ⚠️ THE FLAG IS NOT CLEARED ON FAILURE, and that is upstream's own shape:
        // `loadingSummaryAndPreview = false` is INSIDE the try, after the await, so a failed
        // request leaves the spinner turning forever rather than falling through to "No
        // highlights for this candidate". Clearing it here would be a nicer screen and a
        // different one — the fix belongs upstream first (CLAUDE.md), so this only records it.
        return undefined
      },
    )

    return () => {
      live = false
    }
  }, [candidateId, alertId, viaHunter])

  // The page view and `ShortlistOpened` read this page's candidate off its container upstream;
  // here the profile hands it over (`pageEntity.ts`).
  useEffect(() => {
    if (candidate !== undefined) publishPageEntity('candidate', candidate)
  }, [candidate])

  /**
   * The snippet's `alertId` BINDING, which only the review's side view passes
   * (`alert-id="::…alertId"`) — the standalone profile's label is the text `undefined` even when
   * its URL carries an alert.
   */
  const snippetAlertId = viaHunter ? alertId : undefined
  const onContentScroll = useProfileScrollTracking(candidate, candidate?.id, (current, metrics) => {
    trackCandidateSnippet.profileScroll(current, snippetAlertId, metrics)
  })

  return (
    // The state template's own wrapper — `.shortlist-candidate-profile` is the flex parent in
    // `_extension.state.tabs.scss`, and the side-view div beside the snippet is its sibling
    // rather than its child.
    <div className="shortlist-candidate-profile">
      <CandidateSnippet
        candidate={candidate}
        previewData={preview?.previewData}
        hasMatchingSummary={
          preview?.matchingSummary !== undefined && preview.matchingSummary !== null
        }
        loadingSummaryAndPreview={loadingPreview}
        actions={actions}
        onAction={onProfileAction}
        onSocialUrlClick={(type) => {
          if (candidate !== undefined) trackCandidateSnippet.socialUrl(candidate, snippetAlertId, type)
        }}
        onContentScroll={onContentScroll}
        atsName={atsName}
        {...(atsLabel === undefined ? {} : { atsLabel })}
        relevantDaysForEnriched={relevantDays}
        atsInfoEnabled={atsInfoEnabled}
        // The snippet needs it too, and for two things of its own: the matching-summary card
        // renders only under it, and `socialUrlsDisplay` keeps the LinkedIn circle inside a
        // search even when the user is browsing LinkedIn.
        viaHunter={viaHunter}
        {...(inherited?.alertName === undefined ? {} : { alertName: inherited.alertName })}
        // `hide-show-more-less-button="::true"` — a binding that appears exactly twice in 3,000
        // lines of state config, on this route's hunter twin and on the contact one beside it.
        // Inside the review's side view there is no "More ›" in the snippet header.
        hideShowMoreLessButton={viaHunter}
        viewMore={viewMore}
        onToggleViewMore={onToggleViewMore}
        // `sideBySideStates` — add-to-job, candidate-messages and email-signin, and NOT `more`.
        // While one of them is open the snippet takes `flex-basis: maxWidth` instead of `auto`,
        // so the two halves each hold their width; `more` widens the snippet itself instead.
        sideBySide={child !== null && !onMoreRoute}
        maxSnippetWidth={ExtensionViewWidth.Extended}
        // ⚠️ AND THE WIDTH FOLLOWS `viewMore`, NOT THE PATH, FOR A REASON WORTH SPELLING OUT.
        // The More state's own controller — the one that sets `maxWidth = Extended + Detailed`
        // — lives on the view `shortlist-candidate-profile-view@…`, which sits INSIDE the
        // transclusion that `isInViewMoreState()` gates. False predicate, no transclusion, no
        // ui-view, so that controller never runs and the width never changes. Widening on the
        // path alone stretched the header to 1077px against production's 378 and drew a second
        // copy of the More button at the right-hand edge.
        maxWidth={viewMore ? ExtensionViewWidth.Extended + ExtensionViewWidth.Detailed : ExtensionViewWidth.Extended}
      >
        <div className={`shortlist-candidate-profile-view${viewMore ? ' rendered' : ''}`}>{viewMore ? child : null}</div>
      </CandidateSnippet>
      {/* `rendered` is "a child is active", the same rule the review screen's side view uses —
          without it the div is `flex: 0` and takes no width at all. */}
      <div className={`shortlist-candidate-profile-side-view${child === null || onMoreRoute ? '' : ' rendered'}`}>
        {onMoreRoute ? null : child}
      </div>
      {!sendingDetails || candidate === undefined ? null : (
        <ProfileActionConfirmationModal
          inProgressMessage={CANDIDATE_DETAILS_IN_PROGRESS_MESSAGE}
          okButtonText={PROFILE_ACTION_OK_BUTTON_TEXT}
          run={() => candidateDetailsResultMessage(() => sendCandidateDetails(candidate.id))}
          onClose={() => {
            setSendingDetails(false)
          }}
        />
      )}
      {!reporting || candidate === undefined ? null : (
        <ReportEnrichmentModal
          candidateId={candidate.id}
          candidateName={candidate.name ?? null}
          alertId={alertId ?? null}
          // `$state.current.name == ExtensionState.CandidateProfile` — the standalone profile,
          // 330px wide, gets the slim variant; the review's side view and More get the wide one.
          slim={standaloneState}
          onSubmit={saveEnrichmentReport}
          onSent={() => {
            setReporting(false)
            window.setTimeout(() => {
              setReportSent(true)
            }, REPORT_ENRICHMENT_SENT_DIALOGUE_DELAY_MS)
          }}
          onClose={() => {
            setReporting(false)
          }}
        />
      )}
      {exporting === undefined || candidate === undefined ? null : (
        // ⚠️ MOUNTED ONLY FOR THE RUN, AND IT STARTS THE ATS WRITE ON MOUNT — see the component.
        <SingleProfileExportModal
          key={exporting.key}
          entity="candidates"
          id={candidate.id}
          target={exporting.target}
          {...(atsName === undefined ? {} : { atsName })}
          onOpenInAts={sendOpenHotlist}
          onSettled={onExportSettled}
          onDismissed={() => {
            setExporting(undefined)
          }}
        />
      )}
      {!reportSent ? null : (
        <ReportEnrichmentSentDialogue
          extended={standaloneState}
          onClose={() => {
            setReportSent(false)
          }}
        />
      )}
    </div>
  )
}
